/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { Button, Text, Inline, Icon } from 'sarsaparilla';

import { SearchResultCard } from '../../SearchResultCard/SearchResultCard';
import { CardSurfaceContext } from '../../SearchResultCard/CardSurfaceContext';
import type { SearchResultItem } from '../../../api/search.types';

interface PaginatedPopupCarouselItemProps extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'style' | 'className'
> {
    index: number;
    cardData: SearchResultItem;
}

export const PaginatedPopupCarouselItem = React.forwardRef<
    HTMLDivElement,
    PaginatedPopupCarouselItemProps
>((props, forwardedRef) => {
    const { index, cardData, ...rest } = props;

    return (
        <div
            ref={forwardedRef}
            className="paginated-popup-carousel-item"
            data-item-id={cardData.id}
            {...rest}
        >
            <CardSurfaceContext.Provider value="map-carousel">
                <SearchResultCard item={cardData} isActive />
            </CardSurfaceContext.Provider>
        </div>
    );
});

interface NavigationCardProps extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'style' | 'className'
> {
    onNextPage: undefined | (() => void);
    onPreviousPage: undefined | (() => void);
    dataLength: number;
    pageIndex: number;
    itemsPerPage: number;
}

export const NavigationCard = React.forwardRef<HTMLDivElement, NavigationCardProps>(
    (props, forwardedRef) => {
        const {
            onNextPage,
            onPreviousPage,
            dataLength,
            pageIndex,
            itemsPerPage,
            ...rest
        } = props;
        const startCardNumber = pageIndex * itemsPerPage + 1;
        const endCardNumber = Math.min(startCardNumber + itemsPerPage - 1, dataLength);

        return (
            <div ref={forwardedRef} className="paginated-popup-carousel-item" {...rest}>
                <div className="navigation-card">
                    <div className="navigation-card-inner">
                        <Text size="lg" fontWeight="semibold">
                            {dataLength === 200
                                ? 'More than 200 Results on the Map'
                                : `${dataLength} Results on the Map`}
                        </Text>
                        {dataLength === 200 ? (
                            <Text size="sm" align="center" className="mb-2">
                                We‘re only showing popular matches. Set filters or search
                                a smaller area to refine your search.
                            </Text>
                        ) : null}
                        <Text size="sm">
                            These are results {startCardNumber}-{endCardNumber}
                        </Text>
                        <Inline space="sm" className="mt-2">
                            <Button
                                size="sm"
                                appearance="tertiary-alt"
                                style={{ width: '120px' }}
                                iconBeforeElement={<Icon iconName="chevron-left" />}
                                onClick={onPreviousPage}
                                isDisabled={!onPreviousPage}
                            >
                                Previous
                            </Button>
                            <Button
                                size="sm"
                                appearance="tertiary-alt"
                                style={{ width: '120px' }}
                                iconAfterElement={<Icon iconName="chevron-right" />}
                                onClick={onNextPage}
                                isDisabled={!onNextPage}
                            >
                                Next
                            </Button>
                        </Inline>
                    </div>
                </div>
            </div>
        );
    }
);
